import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { filterDashboardStores } from "../../../access/modules/lud-dashboard";
import { normalizeStoreSummary } from "../../ludDashboard/hooks/useDashboard";
import {
  createDashboardNoteAPI,
  deleteDashboardNoteAPI,
  listDashboardNotesAPI,
  listDashboardStoresAPI,
} from "../../../utils/api/ludDashboard";

// The restaurants the user may reach (the dashboard's list and rule) and the accountant notes of the selected
// restaurant. Notes are only requested for one restaurant: that request is the one the API checks against the user's scope.
export default function useAccountantDirectory(storeId) {
  const token = localStorage.getItem("auth-token");
  const context = useContext(Context);
  const { role } = useAccess();
  const between = context?.storeAccess;
  const betweenKey = JSON.stringify(between || []);
  const [stores, setStores] = useState([]);
  const [storesError, setStoresError] = useState(false);
  const [notes, setNotes] = useState({ status: "idle", rows: [] });

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const ids = JSON.parse(betweenKey);
    listDashboardStoresAPI(token, ids)
      .then((res) => {
        if (cancelled) return;
        setStores(filterDashboardStores(res.stores, { role, between: ids }).map(normalizeStoreSummary));
        setStoresError(false);
      })
      .catch(() => {
        if (!cancelled) setStoresError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token, role, betweenKey]);

  const loadNotes = useCallback(async () => {
    if (!token || !storeId) {
      setNotes({ status: "idle", rows: [] });
      return;
    }
    setNotes((current) => ({ ...current, status: "loading" }));
    try {
      const res = await listDashboardNotesAPI(token, storeId);
      setNotes({ status: "ready", rows: res.notes || [] });
    } catch {
      setNotes({ status: "error", rows: [] });
    }
  }, [token, storeId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const createNote = async (note) => {
    await createDashboardNoteAPI(token, { storeId, note });
    await loadNotes();
  };

  const deleteNote = async (id) => {
    await deleteDashboardNoteAPI(token, id);
    await loadNotes();
  };

  return { stores, storesError, notes, createNote, deleteNote };
}
